import { formatCents, formatCount, tt } from '@agos/shared'

/**
 * *Podporujem* - what the customer's own orders have given to the organizations they support.
 * `FRONTEND.md` §9.784, `BACKEND.md` §10A.437, owner O138g.
 *
 * A customer who typed a team's code did it to support the team. The checkout says the order counts
 * (§9.782); this says what all their orders have come to. Money arrives in cents, as whole numbers.
 */

export const GIVING_PATH = '/api/v1/customer-auth/me/organizations'

export interface GivenTo {
  name: string
  /** The customer's orders with a share in it that can no longer be cancelled. */
  orders: number
  givenCents: number
  /** The share of the customer's orders still open - given once they are delivered or picked up. */
  pendingCents: number
}

export interface Giving {
  organizations: GivenTo[]
  givenCents: number
  pendingCents: number
}

function whole(value: unknown): number {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
  return Number.isFinite(parsed) ? Math.round(parsed) : 0
}

/** The answer, or null when it is not one - which the page reports rather than drawing as nothing given. */
export function givingOf(payload: unknown): Giving | null {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) return null
  const row = payload as Record<string, unknown>
  if (!Array.isArray(row.organizations)) return null
  const organizations = row.organizations
    .filter((one): one is Record<string, unknown> => one !== null && typeof one === 'object')
    .filter((one) => typeof one.name === 'string' && one.name !== '')
    .map((one) => ({
      name: one.name as string,
      orders: whole(one.orders),
      givenCents: whole(one.givenCents),
      pendingCents: whole(one.pendingCents),
    }))
  return { organizations, givenCents: whole(row.givenCents), pendingCents: whole(row.pendingCents) }
}

/**
 * Whether the profile draws the section.
 *
 * **Only for a customer who has supported an organization, or when the read failed.** Most customers never type a
 * team's code, and a section saying *you support nobody* on every profile is noise about a feature their restaurant
 * may not even run. A failed read is drawn - with its sentence and a retry - because drawing nothing would say the
 * same *nobody* from an answer that never came (§9.528).
 */
export function showsGiving(giving: Giving | null, failed: boolean): boolean {
  if (failed) return true
  return giving !== null && giving.organizations.length > 0
}

/** What one organization has had from the customer's orders. */
export function givenSentence(one: GivenTo): string {
  if (one.orders === 0) return tt('Zatiaľ nič - tvoje objednávky s jej kódom ešte nie sú doručené.')
  return `${tt('Tvoje objednávky jej dali')} ${formatCents(one.givenCents)} (${formatCount(one.orders)} ${tt('obj.')})`
}

/** The share still to come, or null when none of the customer's orders with the code is open. */
export function pendingSentence(one: Pick<GivenTo, 'pendingCents'>): string | null {
  if (one.pendingCents <= 0) return null
  return `${tt('Ďalších')} ${formatCents(one.pendingCents)} ${tt('pribudne, keď sa doručia.')}`
}

/** A total line, drawn when the customer supports more than one organization. */
export function totalSentence(giving: Giving): string | null {
  if (giving.organizations.length < 2) return null
  return `${tt('Spolu si podporil(a) organizácie sumou')} ${formatCents(giving.givenCents)}`
}
